import { motion } from "framer-motion";
import { AlertCircle } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { Card } from "@/components/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { queryClient } from "@/lib/react-query";
import { HttpError, isBadRequest } from "@/types/error";
import { PublicOrganisation, ReplayTypeEnum } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  AddonsFormData,
  addonsSchema,
} from "../../organisations/types/organisation-licensing-schema";
import { useUpdateLicense } from "../api/update-license";

import { StartTrialModal } from "./start-trial-modal";

type OrganisationLicenseFormProps = {
  initialData: PublicOrganisation;
};

function transformApiDataToFormData(data: PublicOrganisation): AddonsFormData {
  return {
    concurrency: data.license.concurrency,
    replay: data.license.replay,
    storageInGB: data.license.storageInGB,
    isGivenEventAccess: data.license.isGivenEventAccess,
    storageDurationInDays: data.license.storageDurationInDays,
  };
}

export function AddonsForm(props: OrganisationLicenseFormProps) {
  const { initialData } = props;
  const form = useForm<AddonsFormData>({
    resolver: zodResolver(addonsSchema),
    defaultValues: transformApiDataToFormData(initialData),
  });

  useEffect(() => {
    form.reset(transformApiDataToFormData(initialData));
  }, [initialData, form]);

  const { mutateAsync: updateLicense } = useUpdateLicense({
    onSuccess: (_, { license }) => {
      queryClient.setQueryData<PublicOrganisation | undefined>(
        ["organisation", initialData.id],
        (oldData: PublicOrganisation | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            license: { ...oldData.license, ...license.licenseOverrides },
          };
        },
      );
    },
  });

  async function handleUpdateLicense(data: AddonsFormData) {
    try {
      await updateLicense({
        id: initialData.id,
        license: {
          licenseId: initialData.license.licensePlanId,
          licenseOverrides: data,
        },
      });
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  return (
    <Form {...form}>
      <Card>
        <form onSubmit={form.handleSubmit(handleUpdateLicense)}>
          <Card.Header title="Addons" />
          <Card.Content className="space-y-4">
            <FormField
              control={form.control}
              name="concurrency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Concurrency</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={form.formState.isSubmitting}
                      type="text"
                      placeholder="Enter concurrency"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="storageDurationInDays"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Storage Duration (Days)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={form.formState.isSubmitting}
                      type="text"
                      placeholder="Enter storage duration (Days)"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="replay"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Replay Version</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={form.formState.isSubmitting}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a replay version..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem
                        value={ReplayTypeEnum.LITE}
                        className="capitalize"
                      >
                        Lite
                      </SelectItem>
                      <SelectItem
                        value={ReplayTypeEnum.REPLAY}
                        className="capitalize"
                      >
                        Replay
                      </SelectItem>
                      <SelectItem
                        value={ReplayTypeEnum.ADVANCED}
                        className="capitalize"
                      >
                        Advanced
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <StartTrialModal />
            <FormField
              control={form.control}
              name="storageInGB"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Storage (GB)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={form.formState.isSubmitting}
                      type="text"
                      placeholder="Enter storage (GB)"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div>
              <Label className="text-sm text-muted-foreground">
                Additional Features
              </Label>

              <div className="mt-2 space-y-4">
                <FormField
                  control={form.control}
                  name="isGivenEventAccess"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-row items-center">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="ml-2 mt-0">
                          <FormLabel className="text-base">
                            Enable Event Access
                          </FormLabel>
                        </div>
                      </div>
                    </FormItem>
                  )}
                />

                <div className="flex flex-row items-center">
                  <Switch defaultChecked={false} disabled={true} />
                  <div className="ml-2 mt-0">
                    <Label className="cursor-not-allowed text-base">
                      Enable Marketplace Access
                    </Label>
                  </div>
                </div>
              </div>
            </div>
            {form.formState.errors.root && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Unexpected error</AlertTitle>
                <AlertDescription>
                  {form.formState.errors.root.message}
                </AlertDescription>
              </Alert>
            )}
          </Card.Content>
          <motion.div
            className="overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={
              form.formState.isDirty
                ? { opacity: 1, height: "auto" } // 224px is equivalent to h-56
                : { opacity: 0, height: 0 }
            }
            transition={{ duration: 0.3 }}
          >
            <Card.Footer className="flex gap-4 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  form.reset(transformApiDataToFormData(initialData))
                }
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={form.formState.isSubmitting}
                isAnimated
              >
                Update
              </Button>
            </Card.Footer>
          </motion.div>
        </form>
      </Card>
    </Form>
  );
}
